import { formatBRL, simularParcela, type TabelaTaxas } from '@agencia/shared';
import { logos } from '@/brand';

/**
 * Gera a arte para o cliente (PNG) no próprio navegador: logo Fly Now,
 * foto do voo e as opções de pagamento. Nada é enviado ao servidor.
 */
export interface ArteFoto {
  img: HTMLImageElement;
  /** Ex.: "Ida · LATAM". Vazio = sem etiqueta. */
  rotulo: string;
}

export interface ArteInput {
  fotos: ArteFoto[];
  valor: string; // "1939.68"
  titulo: string;
  mostrarPix: boolean;
  tabela: TabelaTaxas;
}

const W = 1080;
const PAD = 56;
const NAVY = '#101648';
const NAVY_2 = '#1d2466';
const TURQ = '#8fcac6';
const ORANGE = '#fc5a00';
const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('não foi possível abrir a imagem. Use um print em PNG ou JPG.'));
    img.src = src;
  });
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines.slice(0, 2);
}

export async function gerarArte(input: ArteInput): Promise<HTMLCanvasElement> {
  await Promise.all([document.fonts.load(`800 40px ${FONT}`), document.fonts.load(`600 30px ${FONT}`), document.fonts.load(`400 24px ${FONT}`)]).catch(() => undefined);
  const logo = await loadImage(logos.fClaro);
  // Cartão: valor cheio com as taxas da maior parcela embutidas ("até 10x sem juros").
  const maxParcelas = input.tabela.parcelamentoBp.length;
  const cartao = simularParcela(input.valor, maxParcelas, true, input.tabela);

  // Medidas: cabeçalho, foto (proporcional), preços, rodapé.
  if (input.fotos.length === 0) throw new Error('Escolha a foto do voo.');
  const fotoW = W - PAD * 2;
  const innerW = fotoW - 32;
  // Cada foto cabe inteira (sem cortar); com mais fotos, cada uma fica um pouco mais baixa.
  const maxInner = input.fotos.length === 1 ? 1100 : input.fotos.length === 2 ? 820 : 640;
  const ETQ = 58; // faixa da etiqueta (Ida / Volta)
  const blocos = input.fotos.map((f) => {
    const natH = Math.round((f.img.naturalHeight / f.img.naturalWidth) * innerW);
    const drawH = Math.min(maxInner, natH);
    const drawW = Math.round((drawH / natH) * innerW);
    const etq = f.rotulo.trim() ? ETQ : 0;
    return { ...f, drawW, drawH, etq, h: etq + drawH + 32 };
  });
  const GAP = 28;
  const fotosH = blocos.reduce((s, b) => s + b.h, 0) + GAP * (blocos.length - 1);
  const headerH = input.titulo ? 250 : 190;
  const precosH = 36 + 210 + (input.mostrarPix ? 150 : 0) + 30;
  const footerH = 150;
  const H = headerH + fotosH + 40 + precosH + footerH;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Seu navegador não permite gerar imagens.');

  // Fundo azul-marinho com brilho suave.
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0b0f33');
  bg.addColorStop(0.5, NAVY);
  bg.addColorStop(1, '#1b2470');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W * 0.9, 0, 0, W * 0.9, 0, 700);
  glow.addColorStop(0, 'rgba(143,202,198,0.22)');
  glow.addColorStop(1, 'rgba(143,202,198,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Cabeçalho: símbolo F + nome.
  let y = PAD;
  ctx.drawImage(logo, PAD, y - 6, 96, 96);
  ctx.fillStyle = '#ffffff';
  ctx.font = `800 40px ${FONT}`;
  ctx.textBaseline = 'top';
  ctx.fillText('Fly Now Viagens', PAD + 116, y + 10);
  ctx.fillStyle = TURQ;
  ctx.font = `600 24px ${FONT}`;
  ctx.fillText('Sua próxima viagem começa aqui', PAD + 116, y + 58);
  y += 120;
  if (input.titulo) {
    ctx.fillStyle = '#ffffff';
    ctx.font = `800 46px ${FONT}`;
    for (const l of wrap(ctx, input.titulo, W - PAD * 2)) {
      ctx.fillText(l, PAD, y);
      y += 56;
    }
  }
  y = headerH;

  // Fotos do voo, cada uma num cartão branco (com a etiqueta Ida / Volta em cima).
  for (const b of blocos) {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(PAD, y, fotoW, b.h, 28);
    ctx.fill();
    if (b.etq) {
      ctx.font = `800 26px ${FONT}`;
      const t = b.rotulo.trim().toUpperCase();
      const tw = Math.min(ctx.measureText(t).width, innerW - 40);
      ctx.fillStyle = ORANGE;
      ctx.beginPath();
      ctx.roundRect(PAD + 16, y + 16, tw + 40, 42, 21);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.fillText(t, PAD + 36, y + 24, innerW - 40);
    }
    const top = y + 16 + b.etq;
    const left = PAD + 16 + Math.round((innerW - b.drawW) / 2);
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(left, top, b.drawW, b.drawH, 16);
    ctx.clip();
    ctx.drawImage(b.img, left, top, b.drawW, b.drawH);
    ctx.restore();
    y += b.h + GAP;
  }
  y += 40 - GAP;

  // Preços.
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  ctx.beginPath();
  ctx.roundRect(PAD, y, W - PAD * 2, precosH, 28);
  ctx.fill();
  ctx.fillStyle = ORANGE;
  ctx.fillRect(PAD, y, 8, precosH);
  let py = y + 36;
  const px = PAD + 44;
  ctx.fillStyle = TURQ;
  ctx.font = `700 26px ${FONT}`;
  ctx.fillText('NO CARTÃO DE CRÉDITO', px, py);
  ctx.fillStyle = '#ffffff';
  ctx.font = `800 76px ${FONT}`;
  ctx.fillText(formatBRL(cartao.clientePaga), px, py + 36);
  const ate = `em até ${maxParcelas}x sem juros`;
  ctx.fillStyle = ORANGE;
  ctx.font = `800 36px ${FONT}`;
  ctx.fillText(ate, px, py + 128);
  const ateW = ctx.measureText(`${ate} `).width;
  ctx.fillStyle = '#e2e8f0';
  ctx.font = `600 30px ${FONT}`;
  ctx.fillText(`de ${formatBRL(cartao.valorParcela)}`, px + ateW, py + 133);
  py += 210;
  if (input.mostrarPix) {
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(W - PAD - 44, py);
    ctx.stroke();
    py += 28;
    ctx.fillStyle = TURQ;
    ctx.font = `700 26px ${FONT}`;
    ctx.fillText('OU À VISTA NO PIX', px, py);
    ctx.fillStyle = '#ffffff';
    ctx.font = `800 60px ${FONT}`;
    ctx.fillText(formatBRL(input.valor), px, py + 34);
  }

  // Rodapé.
  const hoje = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Bahia' }).format(new Date());
  ctx.fillStyle = NAVY_2;
  ctx.fillRect(0, H - footerH + 20, W, footerH - 20);
  ctx.fillStyle = '#a5b4cf';
  ctx.font = `400 22px ${FONT}`;
  ctx.fillText(`Valores consultados em ${hoje}, sujeitos a alteração e disponibilidade até a emissão.`, PAD, H - footerH + 56);
  ctx.fillStyle = '#ffffff';
  ctx.font = `700 24px ${FONT}`;
  ctx.fillText('flynowviagens.com', PAD, H - 56);
  return canvas;
}
